import React, { Suspense, useEffect } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Ability, EditionNavBanner, Forbidden, FlashInfo, HealthCheckResume, Loader } from '../../../components';
import { updateUi } from '../../../redux';
import { isWhatsNewHash } from '../../../services/whatsNewHint';
import { track_pageview } from '../../../services/posthogAnalytics';
import { LAZY_ROUTES } from './routes';

import './MainContent.scss';

const LazyRouteLoader = () => (
    <div className="MainContent-lazyLoader">
        <Loader color="#666" size={2.5} raw />
    </div>
);

/** React Router 5 used non-exact parent routes as prefixes; v6 needs an explicit splat for nested section routes. */
function toReactRouter6Path(path, exact) {
    if (exact)
        return path;
    if (path === '/')
        return '/';
    return `${path.replace(/\/$/, '')}/*`;
}

/**
 * `Routes key={pathname}` remounts the whole section tree on every URL change.
 * Settings tabs only differ by the last path segment; remounting there reloads tags
 * and resets forms. Keep a stable key for /settings/edit/:id/*.
 * Same for events edit tabs: /events/:type/edit/:eventId/:tabSlug — key must not change
 * when only the tab segment changes or redux-form is destroyed on every NavLink click.
 * Layout editors keep a stable key when switching items under /layouts/edit/:id.
 */
function routesRemountKey(pathname) {
    // Allow nested settings paths (integrations/:key, mobile/:env/:section/…)
    // without remounting SettingEdit / destroying redux-form.
    const settings = pathname.match(/^(\/settings\/edit\/[^/]+)(?:\/.*)?\/?$/);
    if (settings)
        return settings[1];
    const events_edit = pathname.match(/^(\/events\/[^/]+\/edit\/[^/]+)(?:\/[^/]+)?\/?$/);
    if (events_edit)
        return events_edit[1];
    const users_edit = pathname.match(/^(\/users\/edit\/[^/]+)(?:\/[^/]+)?\/?$/);
    if (users_edit)
        return users_edit[1];
    // Place map tabs: /places/edit/:id/map(/zone/...) must not remount PlaceEdit
    // or redux-form is destroyed and loadPlace fires again on every tab click.
    const places_edit = pathname.match(/^(\/places\/edit\/[^/]+)(?:\/.*)?\/?$/);
    if (places_edit)
        return places_edit[1];
    const articles_edit = pathname.match(/^(\/articles\/edit\/[^/]+)(?:\/[^/]+)?\/?$/);
    if (articles_edit)
        return articles_edit[1];
    const tickettypes_edit = pathname.match(/^(\/tickettypes\/edit\/[^/]+)(?:\/.*)?\/?$/);
    if (tickettypes_edit)
        return tickettypes_edit[1];
    const screenings_edit = pathname.match(/^(\/screenings\/edit\/[^/]+)(?:\/[^/]+)?\/?$/);
    if (screenings_edit)
        return screenings_edit[1];
    if (pathname === '/stats/movies' || pathname.startsWith('/stats/movies/movie/'))
        return '/stats/movies';
    if (pathname === '/layouts' || pathname.startsWith('/layouts/edit/'))
        return '/layouts';
    if (pathname === '/email_layouts' || pathname.startsWith('/email_layouts/edit/'))
        return '/email_layouts';
    if (pathname === '/form_layouts' || pathname.startsWith('/form_layouts/edit/'))
        return '/form_layouts';
    return pathname;
}

const MainContent = () => {
    const { collapsedSideBar, isMobile } = useSelector(state => state.ui, shallowEqual);
    const { user }                       = useSelector(state => state.auth);
    const { pathname, hash, search }     = useLocation();
    const navigate                       = useNavigate();
    const dispatch                       = useDispatch();

    useEffect(() => {
        if (isWhatsNewHash(hash)) {
            dispatch(updateUi({ highlightChangelog: true }));
            navigate({ pathname, search, hash: '' }, { replace: true });
            return;
        }
        if (hash === '#changelog')
            dispatch(updateUi({showChangelog: true}));
    }, [dispatch, hash, navigate, pathname, search]);

    useEffect(() => {
        if (!user?.id || !pathname)
            return;
        track_pageview(pathname);
    }, [pathname, user?.id]);

    return (
        <div className={`MainContent${!isMobile ? (collapsedSideBar ? " reducedMainContent" : " expandedMainContent") : ""}`}>
            <Ability can="logs:readOther">
                <HealthCheckResume className=""/>
            </Ability>
            <EditionNavBanner />
            <FlashInfo className=""/>
            {(user.canAccess(pathname)) ?
                /* key forces a full subtree remount on pathname change; avoids stale UI when
                 * leaving heavy views (e.g. React Flow) where the URL updates but the screen
                 * would otherwise keep showing the previous section. */
                <Routes key={routesRemountKey(pathname)}>
                    {LAZY_ROUTES.map(({ path, component: Component, exact }) => (
                        <Route
                            key={path}
                            path={toReactRouter6Path(path, !!exact)}
                            element={(
                                <Suspense fallback={<LazyRouteLoader />}>
                                    <Component />
                                </Suspense>
                            )}
                        />
                    ))}
                </Routes>
                :
                <Forbidden />
            }
        </div>
    );
}

export default MainContent;
